import React, { Component } from 'react'
import { shape, func, bool } from 'prop-types'
import classNames from 'classnames'

import ArtItem from 'Renderer/ArtItem'

import SugarcubeContext from 'apps/SugarcubeContext'
import { artItemType } from 'models/propTypes'
import { withTranslation } from 'react-i18next'
import Tooltip from 'components/Tooltip'
import CompositionItemDraggable from 'components/CompositionItemDraggable'
import ActionPanelContainer from 'containers/design/ActionPanelContainer'

import { workflow } from 'utils/tracker'

import { getRenderer } from 'models/refStore/selectors'
import { getProductTrackingInfo } from 'models/tracking/utils'

require('./CompositionArtItem.scss')

class CompositionArtItem extends Component {
    static optionsMD(e) {
        // Stop the mouseDown if we're clicking
        // on options so that the dragStart doesn't
        // interfere
        e.stopPropagation()
    }

    constructor(props) {
        super(props)
        this.aoeRef = React.createRef()
        this.onRender = this.onRender.bind(this)
        this.RenderObject = null

        this.onDragStart = this.onDragStart.bind(this)
        this.onDrag = this.onDrag.bind(this)
        this.onDragFinish = this.onDragFinish.bind(this)
        this.updateObjectSelected = this.updateObjectSelected.bind(this)
        this.deleteRenderItem = this.deleteRenderItem.bind(this)

        this.onHoverStart = this.onHoverStart.bind(this)
        this.onHoverEnd = this.onHoverEnd.bind(this)
        this.getRenderObject = this.getRenderObject.bind(this)

        this.lastValidPosition = null
        this.wasLastDragValid = false

        this.state = {
            corners: null,
            dragging: false,
            loaded: false,
        }
    }

    componentDidMount() {
        this.renderCanvas(this.props.renderItem.x, this.props.renderItem.y)
        window.addEventListener('keydown', this.deleteRenderItem)
    }

    componentDidUpdate(prevProps, prevState) {
        const { renderItem: artItem } = this.props
        const { renderItem: prevArtItem } = prevProps

        const hasFrameUpdated =
            artItem.size.height !== prevArtItem.size.height ||
            artItem.frame.id !== prevArtItem.frame.id ||
            (artItem.frame.color &&
                (artItem.frame.color.hex !== prevArtItem.frame.color.hex ||
                    artItem.frame.color.material !== prevArtItem.frame.color.material)) ||
            (artItem.frame.matte && artItem.frame.matte.id !== prevArtItem.frame.matte.id)
        if (hasFrameUpdated) {
            this.RenderObject.updateFrame(artItem.frame, artItem.size.height)
        }
        if (
            artItem.x !== prevArtItem.x ||
            artItem.y !== prevArtItem.y ||
            this.props.artImageData !== prevProps.artImageData
        ) {
            this.renderCanvas(artItem.x, artItem.y)
        }
        if (prevArtItem.selected !== artItem.selected || prevState.dragging !== this.state.dragging) {
            this.updateObjectSelected()
        }
    }

    componentWillUnmount() {
        if (this.RenderObject) {
            this.RenderObject.endIsNigh()
            this.RenderObject = null
        }
        window.removeEventListener('keydown', this.deleteRenderItem)
    }

    onRender({ corners, metrics }) {
        if (corners) {
            // TODO: investigate this condition need
            this.setState({
                corners,
            })
        }
        if (metrics) {
            const { isInitialLoading, renderItem } = this.props

            const productTrackingInfo = getProductTrackingInfo(renderItem)

            workflow('DecorateRoom.AddProductLoadTime', {
                ...metrics,
                ...productTrackingInfo,
                LoadTime: metrics.sTotalTime,
                CompositionLoading: isInitialLoading,
            })
            this.setState({ loaded: true })
            this.props.onLoad(metrics)
        }
    }

    onDragStart() {
        if (this.props.onDragStart) {
            this.props.onDragStart()
        }
        if (this.RenderObject) {
            this.RenderObject.onDragStart()
        }
        this.setState({ dragging: true })

        this.props.clearSwapAffinity()
    }

    onDrag({ x, y }) {
        if (!this.RenderObject) {
            return
        }
        const isValid = this.RenderObject.setPosition([x, y])
        if (isValid) {
            this.lastValidPosition = [x, y]
        }

        if (isValid !== this.wasLastDragValid) {
            this.RenderObject.setVisible(isValid)
            this.wasLastDragValid = isValid
        }

        this.props.onDrag({ isValid })
    }

    onDragFinish() {
        const { onDragFinish, update } = this.props

        if (onDragFinish) {
            onDragFinish()
        }

        if (update && this.lastValidPosition && this.lastValidPosition.length) {
            update({
                x: this.lastValidPosition[0],
                y: this.lastValidPosition[1],
            })
            this.RenderObject.setVisible(true)
        }
        if (this.RenderObject) {
            this.RenderObject.onDragFinish()
        }
        this.setState({ dragging: false })
    }

    onHoverStart() {
        const { isMagicWindow, isKiosk } = this.context
        if (isMagicWindow || isKiosk) {
            return
        }
        if (this.RenderObject) {
            this.RenderObject.onHover()
        }
    }

    onHoverEnd() {
        const { isMagicWindow, isKiosk } = this.context
        if (isMagicWindow || isKiosk) {
            return
        }
        if (this.RenderObject) {
            this.RenderObject.onHoverEnd()
        }
    }

    getRenderObject() {
        return this.RenderObject
    }

    updateObjectSelected() {
        if (this.RenderObject) {
            this.RenderObject.setSelected(this.props.renderItem.selected && !this.state.dragging)
        }
    }

    deleteRenderItem(e) {
        const {
            delete: del,
            renderItem: { selected },
        } = this.props
        if (del && selected && (e.keyCode === 8 || e.keyCode === 46)) {
            e.stopPropagation()
            e.preventDefault()
            del()
        }
    }

    renderCanvas(x, y) {
        if (!this.props.artImageData) {
            // Some async stuff still needs to come in
            // The state update will handle it
            return
        }

        const renderer = getRenderer()

        if (!this.RenderObject) {
            const { frame } = this.props.renderItem
            const { height } = this.props.renderItem.size

            this.RenderObject = new ArtItem(
                this.props.renderItem.uniqueId,
                renderer,
                [x, y],
                this.onRender,
                this.props.artImageData,
                frame,
                height,
            )
            this.updateObjectSelected()
            if (this.state.dragging) {
                this.RenderObject.onDragStart()
            }
        }
    }

    render() {
        const {
            delete: del,
            openSettings,
            isUnderCursor,
            renderItem,
            isCurrentDragValid,
            onSelect,
            t,
            disableDrag,
            revealDots,
            showDot,
            duplicate,
        } = this.props
        const { corners, dragging, loaded } = this.state
        const { isKiosk } = this.context

        const { x, y, selected, uniqueId, name, price, description, localItemNumber, region } = renderItem
        const trackingInfo = getProductTrackingInfo(renderItem)

        const renderer = getRenderer()
        if (!renderer) {
            return null
        }

        return (
            <>
                {!disableDrag && (
                    <CompositionItemDraggable
                        className={classNames('CompositionArtItem', { selected })}
                        isUnderCursor={isUnderCursor}
                        corners={corners}
                        renderDimensions={renderer.getRenderDimensions()}
                        onDragStart={this.onDragStart}
                        onDrag={this.onDrag}
                        onDragFinish={this.onDragFinish}
                        onClick={onSelect}
                        x={x}
                        y={y}
                        isCurrentDragValid={isCurrentDragValid}
                        selected={selected}
                        name={name}
                        price={price}
                        description={description}
                        openSettings={openSettings}
                        revealDots={revealDots}
                        getRenderObject={this.getRenderObject}
                        showDot={showDot}
                    >
                        {isKiosk ? null : (
                            <div
                                className="aoe"
                                data-tip={t('tooltip.click to access more options')}
                                data-for={`ttclick${uniqueId}`}
                                onMouseEnter={this.onHoverStart}
                                onMouseLeave={this.onHoverEnd}
                                ref={this.aoeRef}
                            >
                                <Tooltip id={`ttclick${uniqueId}`} hide={selected} />
                            </div>
                        )}
                    </CompositionItemDraggable>
                )}
                {selected && loaded && !dragging && (
                    <ActionPanelContainer
                        targetRef={this.aoeRef}
                        onDelete={del}
                        onOpenSettings={openSettings}
                        onDuplicate={duplicate}
                        localItemNumber={localItemNumber}
                        region={region}
                        trackingInfo={trackingInfo}
                        name={name}
                    />
                )}
            </>
        )
    }
}

CompositionArtItem.contextType = SugarcubeContext
CompositionArtItem.defaultProps = {
    artImageData: null,
    delete: null,
    openSettings: null,
    onDragStart: null,
    onDragFinish: null,
    disableDrag: false,
}

CompositionArtItem.propTypes = {
    renderItem: artItemType.isRequired,
    artImageData: shape({}),
    isUnderCursor: bool.isRequired,

    delete: func,
    openSettings: func,
    update: func.isRequired,
    clearSwapAffinity: func.isRequired,
    duplicate: func.isRequired,

    onDragStart: func,
    onDragFinish: func,
    onDrag: func.isRequired,
    onSelect: func.isRequired,
    onLoad: func.isRequired,
    isCurrentDragValid: bool.isRequired,
    t: func.isRequired,
    isInitialLoading: bool.isRequired,
    disableDrag: bool,
    revealDots: bool.isRequired,
    showDot: bool.isRequired,
}

export default withTranslation()(CompositionArtItem)
